import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthGuard } from '../../guards/auth/auth.guard';
import { MpApiErrorMessageComponent } from '../../../widgets/mp-api-error-message/mp-api-error-message.component';
import { AuthenticationService } from '../../../services/authentication/authentication.service';
import { MpSnackbarService } from '../../../widgets/mp-snackbar/mp-snackbar.service';

export function MpApiErrorInterceptor(request: HttpRequest<unknown>,
                                      next: HttpHandlerFn): Observable<HttpEvent<unknown>> {

  const router: Router = inject(Router);

  const authenticationService: AuthenticationService = inject(AuthenticationService);

  const snackBarService: MpSnackbarService = inject(MpSnackbarService);

  const refreshing$ = new BehaviorSubject<boolean>(false);

  const refreshSuccess = true;

  return next(request).pipe(
    catchError((err) => {
      if (isMagicPalletRequest(request.url)) {
        switch (err.status) {
          case 400:
            openErrorInSnackbar(err, false, snackBarService);
            break;
          case 401:
            return handle401Error(
              err,
              request,
              next,
              router,
              authenticationService,
              refreshing$,
              refreshSuccess);
          case 404:
            break;
          default:
            openErrorInSnackbar(err, true, snackBarService);
            break;
        }
      }
      return throwError(() => err);
    })
  );
}

function openErrorInSnackbar(err: any, autoDisapear = true, snackBar: MpSnackbarService): void {
  const errorKey = err.error.key;
  const variable = err.error.param;
  snackBar.openSnackbarFromComponent(MpApiErrorMessageComponent, {
    duration: autoDisapear ? 2000 : undefined,
    panelClass: 'error-snackbar',
    data: { isFunctionnalError: !autoDisapear, errorKey, variable }
  });
}

function handle401Error(err: any, request: HttpRequest<any>, next: HttpHandlerFn,
                        router: Router,
                        authenticationService: AuthenticationService,
                        refreshing$: BehaviorSubject<boolean>,
                        refreshSuccess: boolean): Observable<HttpEvent<any>> {
  const error = err.error?.message || err.statusText;
  const appLocation = router.routerState.snapshot.url;

  const params = {
    [AuthGuard.REDIRECT_URL]: ''
  };
  params[AuthGuard.REDIRECT_URL] = appLocation;

  if (!refreshing$.value) {
    refreshing$.next(true);

    authenticationService.refreshToken()
      .pipe(
        map(() => true),
        catchError((_) => of(false)))
      .subscribe((res) => {
        refreshing$.next(false);
        refreshSuccess = res;
        if (!refreshSuccess) {
          // refresh token call failed, we redirect to login page
          authenticationService.logout({ path: '/identity/login', extra: { queryParams: params } });
        }
      });
  }

  return refreshing$.pipe(
    filter((refreshing) => !refreshing),
    take(1),
    switchMap((_) =>
      refreshSuccess ? next(request) : throwError(() => error)
    )
  );
}

function isMagicPalletRequest(url: string): boolean {
  return environment.api_base_urls?.ms_defaults?.some((baseUrl) =>
    url.includes(baseUrl)
  );
}
